/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HandHeart, Shield, Wrench, Phone, Star } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { VideoPlayer } from "@/lib/video";
import { Button } from "@/components/ui/button";
import CarCard from "@/components/CarCard";
import api from "@/lib/api";
import { BRAND } from "@/lib/brand";

export default function Home() {
  useSeo(
    "Pit Stop Motors | Quality Used Cars in Nampa, Idaho",
    "Family-owned used car dealership in Nampa, Idaho. Honest deals, quality vehicles, easy financing. Browse our inventory online."
  );
  const [featured, setFeatured] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [lotVideos, setLotVideos] = useState([]);

  useEffect(() => {
    api.get("/cars", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
    api.get("/reviews").then((r) => setReviews((r.data || []).slice(0, 6))).catch(() => {});
    api.get("/lot-videos").then((r) => setLotVideos((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-primary grain" data-testid="home-hero">
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 py-24 md:py-32 lg:py-36 text-primary-foreground">
          <p className="overline !text-accent-bright fade-in-up">Pit Stop Motors · Idaho · Family Owned</p>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-3xl fade-in-up" style={{ animationDelay: "80ms" }}>
            Honest cars.<br />
            <span className="italic text-accent-bright">Idaho built.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-primary-foreground/85 fade-in-up" style={{ animationDelay: "160ms" }}>
            I was born and raised right here in Idaho, and so was this lot. Pit Stop Motors is local, family-owned, and built to serve the neighbors I&rsquo;ve known my whole life &mdash; with honest cars, fair prices, and a real handshake on every deal.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 fade-in-up" style={{ animationDelay: "240ms" }}>
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm h-12 px-8" data-testid="hero-browse-cta">
              <Link to="/inventory">Browse Our Inventory <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-sm h-12 px-8 border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-primary" data-testid="hero-about-cta">
              <Link to="/about">About Us</Link>
            </Button>
          </div>

          {/* Floating contact card */}
          <div className="hidden lg:block absolute right-10 bottom-12 max-w-sm bg-background/95 backdrop-blur-sm border border-soft p-7 fade-in-up" style={{ animationDelay: "320ms" }} data-testid="hero-contact-card">
            <p className="overline">Call the lot</p>
            <a href={BRAND.phoneHref} className="mt-3 block font-serif text-3xl text-primary hover:text-accent transition-colors" data-testid="hero-phone-link">
              {BRAND.phone}
            </a>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Talk to a real person. We&rsquo;re happy to answer questions about any vehicle on the lot.
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="border-y border-soft bg-card" data-testid="home-values">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-16 grid md:grid-cols-3 gap-10">
          {[
            { Icon: HandHeart, title: "Treated Like Family", body: "No high-pressure sales. Just honest conversations and a fair deal for every neighbor who walks in." },
            { Icon: Shield, title: "Inspected & Trusted", body: "Every vehicle is fully inspected by us and cleaned up to give you that fresh car feel." },
            { Icon: Wrench, title: "Backed by Our Word", body: "We tell you the full story of every car — including what we'd fix if it were ours. That's the Pit Stop way." },
          ].map((v, i) => (
            <div key={v.title} className="space-y-3" data-testid={`home-value-${i}`}>
              <v.Icon className="h-7 w-7 text-accent" strokeWidth={1.4} />
              <h3 className="font-serif text-2xl text-primary">{v.title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED INVENTORY */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 py-20" data-testid="home-featured">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline">Featured Inventory</p>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-primary tracking-tight">Cars worth a Sunday drive</h2>
          </div>
          <Link to="/inventory" className="hidden md:inline-flex items-center gap-1 text-sm text-primary hover:text-accent transition-colors" data-testid="featured-view-all">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {featured.length === 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="aspect-[16/10] bg-secondary animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="featured-grid">
            {featured.map((c) => (
              <CarCard key={c.id} car={c} testIdPrefix="featured-card" />
            ))}
          </div>
        )}
      </section>

      {/* MISSION BENTO */}
      <section className="bg-card border-y border-soft" data-testid="home-mission">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 p-8 lg:p-12 border border-soft bg-background">
            <p className="overline">Our Mission</p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-primary leading-tight">
              To make car buying feel like buying from a neighbor &mdash; honest, unhurried, and built on a real handshake.
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Pit Stop Motors is a family-owned, faith-rooted dealership. We believe every customer deserves the truth about the car they're buying, a fair price, and a team that stands behind every deal long after you drive off the lot.
            </p>
            <Button asChild variant="link" className="mt-4 p-0 h-auto text-accent" data-testid="mission-read-more">
              <Link to="/about">Read our full story <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
          <div className="lg:col-span-5 p-8 lg:p-10 border border-soft bg-primary text-primary-foreground flex flex-col justify-center">
            <p className="overline !text-accent-bright">Come See Us</p>
            <h3 className="mt-4 font-serif text-2xl md:text-3xl leading-snug">
              Coffee&rsquo;s on. Conversations are easy. Pressure is non-existent.
            </h3>
            <a href={BRAND.phoneHref} className="mt-5 inline-flex items-center gap-2 text-accent-bright hover:underline" data-testid="mission-phone">
              <Phone className="h-4 w-4" /> {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      {reviews.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 md:px-10 py-20" data-testid="home-reviews">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="overline">Kind Words</p>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl text-primary tracking-tight">What our neighbors say</h2>
            </div>
            <a
              href={BRAND.googleReviewUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 border border-soft px-5 py-2.5 text-sm font-medium text-primary hover:border-accent hover:text-accent transition-colors"
              data-testid="google-review-btn"
            >
              <Star className="h-4 w-4 text-gold fill-gold" /> Review us on Google
            </a>
          </div>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
            {reviews.map((r) => (
              <div key={r.id} className="border border-soft bg-card flex flex-col" data-testid={`review-card-${r.id}`}>
                {r.image_url && (
                  <div className="aspect-[16/9] overflow-hidden bg-secondary">
                    <img src={r.image_url} alt={r.name} className="h-full w-full object-cover" />
                  </div>
                )}
                <div className="p-7">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className={`h-4 w-4 ${i <= r.rating ? "text-gold fill-gold" : "text-border"}`} />
                    ))}
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">&ldquo;{r.text}&rdquo;</p>
                  <p className="mt-5 font-serif text-lg text-primary">— {r.name}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FROM THE LOT — VIDEO BOARD */}
      {lotVideos.length > 0 && (
        <section className="bg-primary grain" data-testid="home-lot-videos">
          <div className="mx-auto max-w-7xl px-6 md:px-10 py-20">
            <p className="overline !text-accent-bright">From the Lot</p>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <h2 className="mt-3 font-serif text-4xl md:text-5xl text-primary-foreground tracking-tight">Latest updates</h2>
              <Link to="/videos" className="inline-flex items-center gap-1 text-sm text-accent-bright hover:underline" data-testid="see-all-videos">
                See all videos <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
              {lotVideos.map((v) => (
                <div key={v.id} className="bg-background border border-soft" data-testid={`lot-video-${v.id}`}>
                  <div className="aspect-video bg-black">
                    <VideoPlayer url={v.video_url} title={v.title} testId={`lot-video-player-${v.id}`} />
                  </div>
                  <div className="p-5">
                    <p className="font-serif text-xl text-primary">{v.title}</p>
                    {v.caption && <p className="mt-1 text-sm text-muted-foreground">{v.caption}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 py-24 text-center" data-testid="home-cta">
        <p className="overline">Come Visit</p>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl text-primary tracking-tight">
          Pull up. Take your time.<br />
          <span className="italic">Drive what feels right.</span>
        </h2>
        <p className="mt-6 max-w-xl mx-auto text-muted-foreground">
          We'd love to meet you. Schedule a test drive, ask about financing, or just say hi.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Button asChild size="lg" className="rounded-sm h-12 px-8" data-testid="cta-contact">
            <Link to="/contact">Contact Us</Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-sm h-12 px-8" data-testid="cta-inventory">
            <Link to="/inventory">See Inventory</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
